import type { CompanyApi } from '@/types/api/companies';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

/**
 * Company type used in the Settings-Company page UI
 */
export interface CompanySettingsUI {
  id: string;
  name: string;
  status: 'active' | 'pending_setup';
  logo?: string;
  description?: string;
  integrationStatus: 'connected' | 'partial' | 'none';
  userCount: number;
  createdAt: string;
  lastSyncedAt?: string;
  // UI convenience aliases for integration linking
  linkedEvoSubscriptionId?: string;
  linkedSubscriptionName?: string;
  linkedProductName?: string;
  linkedDatabaseName?: string;
  linkedSubLedger?: string;
  // Raw backend fields
  companyGroupId?: number;
  code?: string;
  evoSubscriptionId?: string;
  evoOrganisationId?: string;
  linkedSubscriptionId?: string;
  linkedProductId?: string;
  externalCompanyCode?: string;
  externalCompanyName?: string;
  isMultiCompany?: boolean;
  currencyCode?: string;
  taxId?: string;
  address?: string;
  vatRegistrationNumber?: string;
  companyRegistrationNumber?: string;
  phone?: string;
  email?: string;
}

/**
 * Mock companies data for demo mode
 */
const mockCompanies: CompanySettingsUI[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    status: 'active',
    logo: undefined,
    description: 'Leading technology solutions provider',
    integrationStatus: 'connected',
    userCount: 24,
    createdAt: '2024-01-15T10:00:00Z',
    lastSyncedAt: '2025-06-10T08:45:00Z',
    companyGroupId: 10,
    code: 'ACME001',
    currencyCode: 'GBP',
    linkedSubscriptionId: 'sub-1',
    linkedSubscriptionName: 'Demo Instance 1',
    linkedProductName: 'Financials',
    isMultiCompany: true,
    externalCompanyName: undefined,
  },
  {
    id: '1a',
    name: 'Acme Corporation — Trading',
    status: 'active',
    logo: undefined,
    integrationStatus: 'connected',
    userCount: 8,
    createdAt: '2024-01-15T10:00:00Z',
    lastSyncedAt: '2025-06-10T08:45:00Z',
    companyGroupId: 10,
    code: 'ACME001-T',
    currencyCode: 'GBP',
    linkedSubscriptionId: 'sub-1',
    linkedSubscriptionName: 'Demo Instance 1',
    linkedProductName: 'Financials',
    linkedSubLedger: 'SL1',
    isMultiCompany: true,
    externalCompanyName: 'TRADING',
  },
  {
    id: '1b',
    name: 'Acme Corporation — Holdings',
    status: 'active',
    logo: undefined,
    integrationStatus: 'connected',
    userCount: 5,
    createdAt: '2024-01-15T10:00:00Z',
    lastSyncedAt: '2025-06-10T08:45:00Z',
    companyGroupId: 10,
    code: 'ACME001-H',
    currencyCode: 'GBP',
    linkedSubscriptionId: 'sub-1',
    linkedSubscriptionName: 'Demo Instance 1',
    linkedProductName: 'Financials',
    linkedSubLedger: 'SL2',
    isMultiCompany: true,
    externalCompanyName: 'HOLDINGS',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    status: 'active',
    logo: undefined,
    description: 'Manufacturing and distribution',
    integrationStatus: 'connected',
    userCount: 18,
    createdAt: '2024-02-20T14:30:00Z',
    lastSyncedAt: '2025-06-09T16:20:00Z',
    companyGroupId: 20,
    code: 'GLOB002',
    currencyCode: 'EUR',
    linkedSubscriptionName: 'Demo Instance 2',
    linkedProductName: 'DimensionsvNext',
    linkedSubLedger: 'SL3',
    isMultiCompany: false,
  },
  {
    id: '3',
    name: 'Tech Innovations Inc',
    status: 'pending_setup',
    logo: undefined,
    description: 'Software development company',
    integrationStatus: 'none',
    userCount: 12,
    createdAt: '2024-03-10T09:15:00Z',
    lastSyncedAt: undefined,
    companyGroupId: 30,
    code: 'TECH003',
    currencyCode: 'USD',
    linkedSubscriptionName: undefined,
    linkedProductName: undefined,
    linkedSubLedger: undefined,
    isMultiCompany: false,
  },
];

/**
 * Map backend status string to UI status type.
 * The backend can return 'pending_setup' for auto-generated sub-ledger companies.
 */
function mapCompanyStatus(status: string): 'active' | 'pending_setup' {
  if (status === 'pending_setup') return 'pending_setup';
  return 'active';
}

/**
 * Derive integration status from backend fields.
 * A company is 'connected' if it has a linked subscription — externalCompanyCode is not required
 * (it only represents sub-ledger mapping, not the integration link itself).
 * Auto-generated companies are distinguished by their 'pending_setup' status, not by integration status.
 */
function deriveIntegrationStatus(company: CompanyApi): 'connected' | 'partial' | 'none' {
  if (company.linkedSubscriptionId) return 'connected';
  return 'none';
}

/**
 * Map API response to UI format
 */
function mapCompanyApiToUi(company: CompanyApi): CompanySettingsUI {
  return {
    id: String(company.id),
    name: company.name,
    status: mapCompanyStatus(company.status),
    logo: undefined,
    description: undefined,
    // Integration status considers both linked subscription and whether the company has a sub-ledger assigned.
    // Auto-generated companies have linkedSubscriptionId (via their group) but no externalCompanyCode yet,
    // so they should show as pending rather than connected.
    integrationStatus: deriveIntegrationStatus(company),
    userCount: company.assignedUserCount,
    createdAt: company.createdAt,
    lastSyncedAt: company.lastSyncedAt ?? undefined,
    // UI convenience aliases for integration linking
    linkedEvoSubscriptionId: company.linkedSubscriptionId ?? undefined,
    linkedSubscriptionName: company.linkedSubscriptionName ?? undefined,
    linkedProductName: company.linkedProductName ?? undefined,
    linkedDatabaseName: company.linkedDatabaseName ?? undefined,
    linkedSubLedger: company.externalCompanyCode ?? undefined,
    // Raw backend fields
    companyGroupId: company.companyGroupId,
    code: company.code ?? undefined,
    evoSubscriptionId: company.evoSubscriptionId ?? undefined,
    evoOrganisationId: company.evoOrganisationId ?? undefined,
    linkedSubscriptionId: company.linkedSubscriptionId ?? undefined,
    linkedProductId: company.linkedProductId ?? undefined,
    externalCompanyCode: company.externalCompanyCode ?? undefined,
    externalCompanyName: company.externalCompanyName ?? undefined,
    isMultiCompany: company.isMultiCompany,
    currencyCode: company.currencyCode ?? undefined,
    taxId: company.taxId ?? undefined,
    // address, vatRegistrationNumber, companyRegistrationNumber, phone, email are
    // fetched separately via useCompanyDetails when a company dialog is opened
    address: undefined,
    vatRegistrationNumber: undefined,
    companyRegistrationNumber: undefined,
    phone: undefined,
    email: undefined,
  };
}

/**
 * Hook to fetch companies list data for the Settings-Company page
 * Respects demo/live mode - uses mock data in demo mode, API in live mode
 */
export function useCompaniesList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CompanySettingsUI[], Error>(
    ['settings-companies'],
    async ({ signal }) => {
      const response = await apiGet<CompanyApi[]>('/api/v1/companies', undefined, signal);
      return response.map(mapCompanyApiToUi);
    },
    mockCompanies,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
